/**
 * The Zen of Venn — brand tokens.
 *
 * The ONLY file in the repo where raw hex may appear; the repo guard
 * enforces this. Spec: docs/specs/brand.md ("one color, one job").
 */

/** Gelato-marina palette, direction A "La Laguna". */
export const palette = {
  crema: '#FFF8EC',
  laguna: '#0FB5AC',
  fragola: '#FF97B8',
  limone: '#FFE175',
  pesca: '#FFB784',
  inchiostro: '#2E2A26',
} as const;

/** Colors by job. Use these, not the palette, in app code. */
export const color = {
  /** The ground. Every canvas, every card. */
  ground: palette.crema,
  /** The app's only voice: selection glow, cursor, the active hand. */
  identity: palette.laguna,
  /** Circle fills, in set order. */
  scoops: [palette.fragola, palette.limone, palette.pesca],
  /** Every word. Warm, never black-black. */
  ink: palette.inchiostro,
  /**
   * SOMEONE ELSE'S HAND HAS BEEN ON THIS.
   *
   * The sharing design's own word, and its own rule: presence is warmth,
   * never cursors and never digits. This is the one place that warmth is
   * said as a colour — the shelf marks a venn that also lives on a server
   * with it (cowork Phase 3, Task 10), and the presence work (Tasks 5–6)
   * inherits the same value rather than inventing a second one.
   *
   * NOT `identity`, deliberately. Laguna's one job is the ACTIVE hand —
   * the caret, the selection glow, the fill under a hold in progress —
   * and it is cool on purpose so it reads as a tool. A shared venn is not
   * a tool being used; it is a warm fact about who else is here. Pesca is
   * the palette's own warm neutral, already a scoop colour, so the mark
   * belongs to the toy's own family instead of importing a badge colour.
   */
  warmth: palette.pesca,
  /**
   * THE HAND RING — eight colours, one per warmth slot, and the slot is
   * ASSIGNED BY THE ROOM rather than derived from who you are.
   *
   * That is the whole design of this list and it is a privacy rule
   * before it is an aesthetic one: the spec's flat sentence is "hand
   * colours are assigned by the DO per venn per session from a fixed
   * small ring — NEVER derived from identity", because a colour derived
   * from a person is a tracking cookie you can see. `assignWarmth`
   * (`live.ts`) hands out the LOWEST FREE SLOT and takes no identity
   * argument at all; this array is the only thing that turns its answer
   * into ink.
   *
   * THE ORDER IS A FARTHEST-POINT TRAVERSAL, not a walk round the
   * wheel — and this is the correction that matters most in the file.
   * An earlier printing of this list ordered the colours by hue and
   * claimed that "slots 0 and 1 are the two least likely to be mistaken
   * for one another". Measured, that was exactly inverted: lampone
   * (336.9°) and ciliegia (351.6°) sat 14.7° apart, the SMALLEST
   * adjacent gap in the ring, and CIE76 ΔE 23.0 — the closest of all 28
   * pairs. Because slots are handed out lowest-first, the first two
   * hands in every room in the world would have worn the two colours
   * hardest to tell apart, over pastel fills, breathing in phase.
   * Telling two hands apart is this round's entire success condition.
   *
   * So the order is chosen by measurement: slot 1 is the colour
   * FURTHEST from slot 0, and each later slot is the one whose nearest
   * neighbour among all slots already handed out is as far away as
   * possible.
   *
   * WHAT THAT BUYS, EXACTLY — and the precision matters, because an
   * earlier printing of this paragraph claimed the ordering was optimal
   * "for EVERY room size" and it is not. A farthest-point traversal is
   * a greedy 2-approximation, not an exact max-min solver, and Bianca
   * brute-forced all 254 subsets to show the difference. The true
   * sentence:
   *
   *   THE FIRST TWO HANDS ARE PROVABLY THE RING'S WIDEST PAIR, which is
   *   the case that actually happens and the one this whole reordering
   *   was for. Every room size is seated optimally except three, and
   *   those three are short by a margin no eye can find.
   *
   * Measured, CIE76 ΔE (the numbers this comment is allowed to claim,
   * all reproduced against the bytes above rather than remembered):
   *
   *   slots 0-1  zafferano / mirtillo   174.9°   ΔE 140.9  (the ring's
   *                                                        widest pair)
   *   smallest ADJACENT-SLOT gap        60.0°    ΔE  53.5  (6-7)
   *   closest pair anywhere             slots 2 & 7        ΔE  23.0
   *
   *   k   seated   best possible          of optimal
   *   2   140.86   140.86  (0,1)          1.000
   *   3    81.30    95.19  (0,4,7)        0.854
   *   4    56.82    65.59  (1,3,5,7)      0.866
   *   5    56.76    56.82  (0,2,3,4,6)    0.999
   *   6    39.59    39.59                 1.000
   *   7    26.31    26.31                 1.000
   *   8    23.03    23.03                 1.000
   *
   * Worst case 0.854 of optimal, at three hands, where the seated
   * minimum is still 81.3 — a gulf. Re-solving for exact max-min would
   * churn a ring that works for a distinction nobody's eye can find,
   * and would trade away the one guarantee that is worth having: the
   * first pair.
   *
   * The 23.0 pair (ciliegia and lampone, the two reds) still exists —
   * eight distinct colours in a warm gelato register is a real
   * constraint — but it now needs SEVEN hands in one venn before both
   * are on screen, instead of two.
   *
   * ON LAGUNA. `identity` is laguna and laguna has exactly one job:
   * where YOUR hand is. The ring is pushed away from it but does not
   * clear it entirely — cielo is its nearest approach at ΔE 34.7, and
   * pistacchio is next at 44.8. Cielo sits at slot 4, so it takes five
   * hands in one room before it appears at all, and the two marks never
   * do the same thing in the same place (laguna is a glow traced along
   * a region's boundary; a hand colour is a stroke around a whole
   * scoop). An earlier printing claimed the ring "never walks through
   * the teal", which was simply false; this says the distance instead.
   *
   * Eight is `HAND_RING_SIZE`, and `handColor` asserts the two agree
   * rather than trusting this comment — raise the ring without adding a
   * colour and slot 8 would silently wear slot 0's ink.
   *
   * Saturated to gelato like the rest of the palette, and lighter than
   * ink: these are strokes drawn ON the scoop fills (fragola, limone,
   * pesca), so every one has to hold its own against a warm pastel
   * without going muddy.
   */
  hands: [
    /** zafferano — saffron */
    '#F2C230',
    /** mirtillo — blueberry. Slot 1 by measurement: the furthest
     *  colour in the ring from slot 0, 174.9° and ΔE 140.9 away. */
    '#5B72E8',
    /** ciliegia — cherry */
    '#E2455B',
    /** pistacchio */
    '#7CC46A',
    /** cielo — sky */
    '#46B9E8',
    /** mandarino — tangerine */
    '#FF8A3D',
    /** lavanda */
    '#B673E0',
    /** lampone — raspberry */
    '#F5478A',
  ],
  /** Designed lens colors per scoop pair — never raw color math. */
  lens: {
    /** fragola × limone — the first kiss */
    pair01: '#FF8E62',
    /** fragola × pesca — both already warm pinks/peaches, so their kiss
     *  leans further into that family: a deeper, more saturated coral-red
     *  than either parent, not a pale average of the two. */
    pair02: '#FF7A5C',
    /** limone × pesca — yellow meeting peach lands as a warm, saturated
     *  amber-gold, brighter and more gilded than a naive blend. */
    pair12: '#FFAB4A',
    /** fragola × limone × pesca — the heart where all three meet. The
     *  rarest, most specific region earns the warmest, most saturated
     *  color of the four: a vivid tomato-red the three pairs visibly
     *  converge toward. */
    triple: '#FF6347',
  },
} as const;

/**
 * Motion — "la goccia". Stillness is the only time it is perfectly round.
 * CSS reference curves; in-app springs must reproduce this personality.
 */
export const motion = {
  /** Quad accelerate out of stillness. */
  glideIn: [0.11, 0, 0.5, 0],
  /** Quad decelerate, slope-matched to glideIn at the handoff. */
  glideOut: [0.5, 1, 0.89, 1],
  /** Sine in-out for segments between physical turning points. */
  turn: [0.37, 0, 0.63, 1],
  /**
   * Long-axis scale of a circle at peak drag velocity. Bench-tuned
   * 2026-08-21.
   *
   * This names the LONG AXIS ONLY. Area is conserved, so the short axis is
   * divided by the same factor and the drawn shape's bounding box reaches
   * an aspect ratio of maxStretch² — 1.124 at this value. Tune this number
   * when you mean "how much longer", not "how much more oval".
   */
  maxStretch: 1.075,
} as const;

export type Palette = typeof palette;
export type Color = typeof color;
export type Motion = typeof motion;
